import React from 'react';
import { useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { Box } from '../Box';
import { AbstractButton } from '../AbstractButton';

export interface OverflowTextWithViewMoreProps {
  children: React.ReactNode;
  numLines?: number;
  sx?: SxProps<Theme>;
}

export const OverflowTextWithViewMore: React.FC<OverflowTextWithViewMoreProps> = ({
  children,
  numLines = 1,
  sx,
}) => {
  const theme = useTheme();
  const { componentTokens } = theme.palette;

  const [isOverflowed, setIsOverflowed] = React.useState(false);
  const [isHidden, setIsHidden] = React.useState(true);

  const textRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setIsHidden(true);
    const el = textRef.current;
    if (el) {
      setIsOverflowed(el.offsetWidth < el.scrollWidth || el.offsetHeight < el.scrollHeight);
    }
  }, [children, numLines]);

  const clampSx: SxProps<Theme> = {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: numLines,
    overflow: 'hidden',
    wordWrap: 'break-word',
  };

  if (!isOverflowed) {
    return (
      <Box ref={textRef} sx={{ wordWrap: 'break-word', ...clampSx, ...sx }}>
        {children}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', ...sx }}>
      <Box ref={textRef} sx={isHidden ? clampSx : { wordWrap: 'break-word' }}>
        {children}
      </Box>
      <AbstractButton
        onClick={() => setIsHidden(h => !h)}
        sx={{
          display: 'inline',
          alignSelf: 'flex-end',
          color: componentTokens.buttons.colors.link.primary.text.resting,
        }}
      >
        {isHidden ? 'View More' : 'View Less'}
      </AbstractButton>
    </Box>
  );
};
